import { useEffect, useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useDispatch, useSelector } from 'react-redux'
import { find, isEmpty, map, some } from 'lodash'

import { useQueryParams } from '../../core/hooks'

import {
  COMMON_AVAILABLE_HOTKEYS,
  STATUS_FLAG_FIELD,
} from '../../common/constants'
import { useRowHighlight } from '../../common/hooks'

import { LOCATIONS_AVAILABLE_HOTKEYS } from '../constants'
import { updateLocationTableHotkeys } from '../redux/actions'

import useUpdateLocationsStatusFlagsMutation from './use-update-locations-status-flags-mutation'

const useLocationTableHotkeyHighlights = (
  isDisabled,
  isTaskCurationDone,
  isTaskQaDone,
) => {
  const [isNavigationEnabled, setNavigationEnabled] = useState(false)
  const dispatch = useDispatch()
  const {
    isLoading,
    data,
    hotkeys: { selectedRowKey, selectedCellKey, isHotkeyEditModeEnabled },
  } = useSelector(store => store.locations.locations)
  const { result: locations } = data || {}

  const { pageSize, currentPage } = useQueryParams()

  const areHotkeysDisabled = useMemo(
    () => isHotkeyEditModeEnabled || !isNavigationEnabled || isDisabled,
    [isDisabled, isHotkeyEditModeEnabled, isNavigationEnabled],
  )

  const isRowSelectionDisabled = useMemo(
    () => !isNavigationEnabled || isDisabled,
    [isDisabled, isNavigationEnabled],
  )

  const isNavigationReadyToBeEnabled = useMemo(
    () => !isDisabled && !isNavigationEnabled,
    [isDisabled, isNavigationEnabled],
  )

  const { initHighlight } = useRowHighlight({
    tableSelector: '.locations-table .ant-table-container table',
    rowsSelector:
      '.locations-table .ant-table-container table tbody > tr:not(.ant-table-measure-row)',
    cellsSelector: `.locations-table .ant-table-container table tbody > tr:not(.ant-table-measure-row) td.cell-editable`,
    scrollContainerSelector: '.locations-table .ant-table-body',
    areHotkeysDisabled,
    isRowSelectionDisabled,
    updateHotkeysAction: updateLocationTableHotkeys,
  })

  useEffect(() => {
    setTimeout(initHighlight, 0)
    dispatch(updateLocationTableHotkeys({ isHotkeyEditModeEnabled: false }))
  }, [isLoading, initHighlight, pageSize, currentPage, dispatch])

  useHotkeys(
    COMMON_AVAILABLE_HOTKEYS.DOWN.hotkey,
    () => {
      setNavigationEnabled(true)
      const [scrollContainer] = document.querySelectorAll(
        '.locations-table .ant-table-body',
      )
      if (scrollContainer) {
        scrollContainer.scrollIntoView()
      }
    },
    {
      enabled: isNavigationReadyToBeEnabled,
    },
    [isNavigationReadyToBeEnabled],
  )

  const statusFlagFieldName = useMemo(() => {
    if (!isTaskCurationDone) {
      return STATUS_FLAG_FIELD.IS_CURATION_DONE
    }

    if (!isTaskQaDone) {
      return STATUS_FLAG_FIELD.IS_QA_DONE
    }

    return STATUS_FLAG_FIELD.IS_QC_DONE
  }, [isTaskCurationDone, isTaskQaDone])

  const [updateStatusFlag] =
    useUpdateLocationsStatusFlagsMutation(statusFlagFieldName)

  useHotkeys(
    LOCATIONS_AVAILABLE_HOTKEYS.S.hotkey,
    () => {
      const location = find(locations, ({ id }) => id === selectedRowKey)

      let isDone = !location.isQcDone

      if (!isTaskCurationDone) {
        isDone = !location.isCurationDone
      }

      if (!isTaskQaDone) {
        isDone = !location.isQaDone
      }

      if (!isEmpty(location)) {
        updateStatusFlag([selectedRowKey], isDone)
      }
    },
    {
      enabled: !areHotkeysDisabled,
    },
    [
      locations,
      selectedRowKey,
      areHotkeysDisabled,
      isTaskCurationDone,
      isTaskQaDone,
      updateStatusFlag,
    ],
  )

  useHotkeys(
    LOCATIONS_AVAILABLE_HOTKEYS.SHIFT_S.hotkey,
    () => {
      const locationIds = map(locations, 'id')

      const currentValue = !some(locations, location => {
        if (!isTaskCurationDone) {
          return !location.isCurationDone
        }

        if (!isTaskQaDone) {
          return !location.isQaDone
        }

        return !location.isQcDone
      })

      updateStatusFlag(locationIds, !currentValue)
    },
    {
      enabled: !areHotkeysDisabled,
    },
    [locations, areHotkeysDisabled, isTaskCurationDone, updateStatusFlag],
  )

  useHotkeys(
    LOCATIONS_AVAILABLE_HOTKEYS.ENTER.hotkey,
    event => {
      event.preventDefault()
      dispatch(updateLocationTableHotkeys({ isHotkeyEditModeEnabled: true }))
    },
    {
      enabled: !areHotkeysDisabled,
    },
    [areHotkeysDisabled, selectedRowKey, selectedCellKey],
  )
}

export default useLocationTableHotkeyHighlights
